/* =====================================================================
   GameYard — shared storefront JS
   Loaded once (defer) from layout/theme.liquid. Everything is
   feature-detected so it is safe on any template, and re-runnable so
   it keeps working inside the Shopify theme editor (section reloads).
   ===================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Reveal on scroll ---------------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('.gy-reveal:not([data-gy-revealed])');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('gy-in'); el.setAttribute('data-gy-revealed', ''); });
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('gy-in');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach(function (el) { el.setAttribute('data-gy-revealed', ''); io.observe(el); });
  }

  /* ---------------- Sticky header on scroll ----------------
     The header lives in a short section wrapper, so CSS `position: sticky`
     cannot keep it pinned for the whole page (its containing block is too
     short). Instead, when the section opts in (.gy-header--fixed), the
     header is position:fixed and we insert an in-flow spacer of equal
     height so page content is never hidden and there is no layout jump
     when the header auto-hides on scroll. */
  function initHeader() {
    var header = document.querySelector('[data-gy-header]');
    if (!header || header.__gyBound) return;
    header.__gyBound = true;

    var fixed = header.classList.contains('gy-header--fixed');
    var spacer = null;

    if (fixed) {
      spacer = document.createElement('div');
      spacer.className = 'gy-header-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      if (header.parentNode) header.parentNode.insertBefore(spacer, header.nextSibling);

      var syncSpacer = function () { spacer.style.height = header.offsetHeight + 'px'; };
      syncSpacer();
      window.addEventListener('resize', syncSpacer, { passive: true });
      window.addEventListener('load', syncSpacer);
      if ('ResizeObserver' in window) { new ResizeObserver(syncSpacer).observe(header); }
      // Re-sync after web fonts settle.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncSpacer);
      window.setTimeout(syncSpacer, 350);
    }

    var lastY = window.scrollY;
    var ticking = false;
    var hideOnScroll = header.getAttribute('data-gy-hide-on-scroll') === 'true';

    function onScroll() {
      var y = window.scrollY;
      header.classList.toggle('gy-header--scrolled', y > 12);
      if (hideOnScroll) {
        if (y > lastY && y > 240) header.classList.add('gy-header--hidden');
        else header.classList.remove('gy-header--hidden');
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
    onScroll();
  }

  /* ---------------- Mobile menu drawer ---------------- */
  function initMobileMenu() {
    var drawer = document.querySelector('[data-gy-menu]');
    if (!drawer || drawer.__gyBound) return;
    drawer.__gyBound = true;

    var openBtns = document.querySelectorAll('[data-gy-menu-open]');
    var closeEls = drawer.querySelectorAll('[data-gy-menu-close]');
    var firstLink;

    function open() {
      drawer.classList.add('gy-menu--open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('gy-no-scroll');
      openBtns.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      firstLink = drawer.querySelector('a, button');
      if (firstLink) firstLink.focus();
      document.addEventListener('keydown', onKey);
    }
    function close() {
      drawer.classList.remove('gy-menu--open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('gy-no-scroll');
      openBtns.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); b.focus && b.focus(); });
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }

    openBtns.forEach(function (b) { b.addEventListener('click', open); });
    closeEls.forEach(function (b) { b.addEventListener('click', close); });

    /* expandable sub-menus inside the drawer */
    drawer.querySelectorAll('[data-gy-submenu-toggle]').forEach(function (t) {
      t.addEventListener('click', function () {
        var parent = t.closest('[data-gy-submenu]');
        if (!parent) return;
        var isOpen = parent.classList.toggle('gy-submenu--open');
        t.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });
  }

  /* ---------------- FAQ / accordion ---------------- */
  function initAccordions(root) {
    var groups = (root || document).querySelectorAll('[data-gy-accordion]:not([data-gy-acc-bound])');
    groups.forEach(function (group) {
      group.setAttribute('data-gy-acc-bound', '');
      var single = group.getAttribute('data-gy-accordion-single') !== 'false';
      var items = group.querySelectorAll('details');
      items.forEach(function (item) {
        var summary = item.querySelector('summary');
        if (!summary) return;
        summary.addEventListener('click', function () {
          if (single && !item.open) {
            items.forEach(function (other) { if (other !== item) other.open = false; });
          }
        });
      });
    });
  }

  /* ---------------- Sticky add-to-cart ---------------- */
  function initStickyAtc() {
    var bar = document.querySelector('[data-gy-sticky-atc]');
    if (!bar || bar.__gyBound) return;
    bar.__gyBound = true;

    var mainBtn =
      document.querySelector('[data-gy-atc-anchor] [type="submit"]') ||
      document.querySelector('form[action*="/cart/add"] [type="submit"]');
    var anchor = document.querySelector('[data-gy-atc-anchor]') || mainBtn;
    var stickyBtn = bar.querySelector('[data-gy-sticky-atc-button]');

    if (!mainBtn || !anchor) { bar.remove(); return; }

    function show() { bar.classList.add('gy-sticky-atc--visible'); }
    function hide() { bar.classList.remove('gy-sticky-atc--visible'); }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { entry.isIntersecting ? hide() : show(); });
      }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });
      io.observe(anchor);
    }

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        mainBtn.click();
      });
    }
  }

  /* ---------------- Quantity stepper ---------------- */
  function initQty(root) {
    var steppers = (root || document).querySelectorAll('[data-gy-qty]:not([data-gy-qty-bound])');
    steppers.forEach(function (wrap) {
      wrap.setAttribute('data-gy-qty-bound', '');
      var input = wrap.querySelector('[data-gy-qty-input]');
      var minus = wrap.querySelector('[data-gy-qty-minus]');
      var plus = wrap.querySelector('[data-gy-qty-plus]');
      if (!input) return;
      function clamp(v) {
        var min = parseInt(input.min, 10) || 1;
        var max = parseInt(input.max, 10) || Infinity;
        if (isNaN(v) || v < min) v = min;
        if (v > max) v = max;
        return v;
      }
      if (minus) minus.addEventListener('click', function () {
        input.value = clamp((parseInt(input.value, 10) || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (plus) plus.addEventListener('click', function () {
        input.value = clamp((parseInt(input.value, 10) || 1) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      input.addEventListener('change', function () { input.value = clamp(parseInt(input.value, 10)); });
    });
  }

  /* ---------------- Product gallery (custom) ---------------- */
  function initGallery(root) {
    var galleries = (root || document).querySelectorAll('[data-gy-gallery]:not([data-gy-gallery-bound])');
    galleries.forEach(function (g) {
      g.setAttribute('data-gy-gallery-bound', '');
      var stage = g.querySelector('[data-gy-gallery-stage]');
      var thumbs = g.querySelectorAll('[data-gy-gallery-thumb]');
      if (!stage) return;
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var id = thumb.getAttribute('data-media-id');
          var slides = stage.querySelectorAll('[data-media-id]');
          slides.forEach(function (s) {
            var active = s.getAttribute('data-media-id') === id;
            s.classList.toggle('gy-gallery__slide--active', active);
          });
          thumbs.forEach(function (t) {
            t.classList.toggle('gy-gallery__thumb--active', t === thumb);
            t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
          });
        });
      });
    });
  }

  /* ---------------- Boot ---------------- */
  function boot(root) {
    initHeader();
    initMobileMenu();
    initReveal(root);
    initAccordions(root);
    initStickyAtc();
    initGallery(root);
    initQty(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Re-init the affected section inside the Shopify theme editor */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function () { boot(document); });
})();
